/**
 * Blocos da Home que a pessoa pode reordenar e desligar na gaveta "Personalizar Dashboard" (card 3788). Os ids são da
 * tela (o backend só confere o formato) e não mudam: estão gravados na arrumação de cada usuário. Bloco novo no futuro
 * entra aqui e aparece no fim da Home de quem já personalizou (`resolveHomeLayout`).
 *
 * A barra do Cog não é bloco: fica sempre no topo.
 */
export type HomeBlockId =
  | 'economy'
  | 'calendar'
  | 'performance'
  | 'credit-score'
  | 'bank-limits'
  | 'meetings';

/** `half`: divide a linha com o próximo bloco "metade"; `full`: ocupa a linha inteira. */
export type HomeBlockWidth = 'half' | 'full';

export interface HomeBlockDefinition {
  id: HomeBlockId;
  /** Rótulo na gaveta. */
  label: string;
  width: HomeBlockWidth;
}

/** Um bloco na arrumação da Home: id e se está ligado. */
export interface HomeLayoutItem {
  id: HomeBlockId;
  visible: boolean;
}

/** Blocos na ordem padrão (a do protótipo aprovado). */
export const HOME_BLOCKS: readonly HomeBlockDefinition[] = [
  { id: 'economy', label: 'Economia Prevista', width: 'half' },
  { id: 'calendar', label: 'Calendário de Vencimentos', width: 'half' },
  { id: 'performance', label: 'Performance da Dívida', width: 'full' },
  { id: 'credit-score', label: 'Score de Crédito', width: 'full' },
  { id: 'bank-limits', label: 'Limites Bancários', width: 'half' },
  { id: 'meetings', label: 'Reuniões', width: 'half' },
];

/** Ids na ordem padrão. */
export const DEFAULT_HOME_BLOCK_IDS: readonly HomeBlockId[] = HOME_BLOCKS.map(block => block.id);

const BLOCKS_BY_ID = new Map<string, HomeBlockDefinition>(
  HOME_BLOCKS.map(block => [block.id, block])
);

/**
 * Diz se o id é de um bloco que a Home conhece.
 * @param id Id lido da arrumação gravada.
 */
export function isHomeBlockId(id: unknown): id is HomeBlockId {
  return typeof id === 'string' && BLOCKS_BY_ID.has(id);
}

/**
 * Definição do bloco (rótulo e largura).
 * @param id Id do bloco.
 */
export function homeBlockDefinition(id: HomeBlockId): HomeBlockDefinition {
  return BLOCKS_BY_ID.get(id) as HomeBlockDefinition;
}
